import useLanguage, { useLanguagesData } from 'FlowBuilder/hooks/useLanguageFlow';
import imageUrlFromSignedBlobId from 'Shared/functions/imageUrlFromSignedBlobId'
import './InlineEditableField.scoped.scss'

export default function InlineEditableField({ field, value, onClick, translatable = false, defaultValue = null }) {
  const [isHovered, setIsHovered] = useState(false)
  const editIconStyles = {
    cursor: 'pointer',
    position: 'absolute',
    top: '0',
    right: '0px',
    width: translatable ? '40px' : '20px',
    height: '20px',
    backgroundColor: '#cccccccc',
    borderRadius: '3px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10
  }

  // Determine parameters based on field type
  const hasImage = field.image;
  const isWysiwyg = field.wysiwyg;
  const minimumHeight = hasImage ? '100px' : '30px';
  const placeholderText = defaultValue ?? (hasImage ? <p style={{ marginLeft: '10px', marginTop: '0px', marginBottom: '0px' }}>Add an image</p> : 'Add some content');

  // Translatable styling
  const language = useLanguage();
  const { editableLanguagesData } = useLanguagesData();
  const hasTranslations = translatable && editableLanguagesData && Object.keys(editableLanguagesData).length > 1;
  const flag = language && hasTranslations ? editableLanguagesData[language]?.flag : null;

  // Get wrapper styles with parameters
  const wrapperStyles = getFieldWrapperStyles({
    hasImage,
    minimumHeight,
    isHovered
  });

  // Additional wrapper style for image to support absolute positioning of placeholder
  const wrapperWithPosition = hasImage && !value ? { ...wrapperStyles, position: 'relative' } : wrapperStyles;

  // Placeholder component to show when no value exists
  const Placeholder = !value && (
    <div style={{
      opacity: defaultValue ? 1 : 0.6,
      fontSize: '16px',
      ...(hasImage ? {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        position: 'absolute',
        top: 0,
        left: 0
      } : {})
    }}>
      {hasImage && <SvgIconsDownload width={24} height={24} />}
      {placeholderText}
    </div>
  );

  // Function to render content based on field type
  const renderContent = () => {
    if (!value) {
      return Placeholder;
    }

    if (isWysiwyg) {
      return <div dangerouslySetInnerHTML={{ __html: value }} />;
    }

    if (hasImage) {
      if ((value || '').includes('https://') || (value || '').includes('data:image/webp;base64')) {
        return <img src={value} alt={field.label} />;
      }
      return <img src={imageUrlFromSignedBlobId(value)} alt={field.label} />;
    }

    return value;
  };

  return (
    <div
      className="inline-editable-field"
      style={wrapperWithPosition}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      {renderContent()}
      <span style={editIconStyles} onClick={(e) => {
        e.stopPropagation(); // Prevent double triggering
        onClick(e);
      }}>
        <span style={{ fontSize: '16px' }}>{translatable && flag}</span>
        <SvgIconsEdit color={'currentColor'} style={{ color: 'white' }} />
      </span>
    </div>
  );
}

function getFieldWrapperStyles({ hasImage, minimumHeight, isHovered }) {
  const baseStyles = {
    border: '1px solid #ccc',
    borderRadius: '3px',
    padding: '0px 5px',
    position: 'relative',
    minHeight: minimumHeight,
    backgroundColor: isHovered ? '#ccc' : 'transparent',
    transition: 'background-color 0.2s',
    cursor: isHovered ? 'pointer' : 'default'
  };

  // Add additional styles for image fields
  if (hasImage) {
    return {
      ...baseStyles,
      width: '100%',
      display: 'block'
    };
  }

  return baseStyles;
}
